/**
 * 🔧 Servicio para Contenido Guardado de Studios
 * Maneja todas las operaciones CRUD para contenido guardado
 */

import { apiService } from './api';
import { authManager } from '../utils/authManager';
import logger from '../utils/simpleLogger';

// Interfaces para el contenido guardado
export interface SavedContent {
  id: string;
  user_id: string;
  studio_id: string;
  studio_name: string;
  studio_type: string;
  title: string;
  content: any;
  meta_data?: Record<string, any>;
  document_ids?: string[];
  document_names?: string[];
  tags?: string[];
  is_shared: boolean;
  shared_at?: string;
  shared_link?: string;
  created_at: string;
  updated_at: string;
}

export interface SavedContentListResponse {
  success: boolean;
  content: SavedContent[];
  total: number;
  page: number;
  per_page: number;
}

export interface SavedContentCreateRequest {
  title: string;
  studio_id: string;
  studio_name: string;
  studio_type: string;
  content: any;
  meta_data?: Record<string, any>;
  document_ids?: string[];
  document_names?: string[];
  tags?: string[];
}

export interface SavedContentUpdateRequest {
  title?: string;
  tags?: string[];
  is_shared?: boolean;
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '');
let hasLoggedMissingEndpoint = false;

class SavedContentService {
  private baseUrl = `${API_BASE}/saved-content`;

  /**
   * 🔍 Obtener lista de contenido guardado
   */
  async getSavedContent(params?: {
    page?: number;
    per_page?: number;
    studio_id?: string;
  }): Promise<SavedContentListResponse> {
    logger.info('🔍 SavedContentService: Obteniendo contenido guardado...');
    logger.info('📋 Parámetros:', params);

    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
      if (params?.studio_id) queryParams.append('studio_id', params.studio_id);

      const url = `${this.baseUrl}?${queryParams.toString()}`;
      logger.info('🌐 URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: authManager.getAuthHeaders(),
      });

      if (response.status === 404) {
        if (!hasLoggedMissingEndpoint) {
          console.warn(
            'ℹ️ SavedContentService: Endpoint /saved-content no disponible (404). Retornando lista vacía.'
          );
          hasLoggedMissingEndpoint = true;
        }
        return {
          success: false,
          content: [],
          total: 0,
          page: params?.page ?? 1,
          per_page: params?.per_page ?? 20,
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      logger.info('✅ Respuesta recibida:', data);
      return data as SavedContentListResponse;
    } catch (error: any) {
      const isNetworkError =
        error instanceof TypeError ||
        error?.name === 'TypeError' ||
        error?.message?.toLowerCase().includes('failed to fetch');

      if (isNetworkError) {
        if (!hasLoggedMissingEndpoint) {
          console.warn(
            'ℹ️ SavedContentService: No se pudo contactar al backend /saved-content (posible CORS o endpoint ausente). Retornando lista vacía.'
          );
          hasLoggedMissingEndpoint = true;
        }

        return {
          success: false,
          content: [],
          total: 0,
          page: params?.page ?? 1,
          per_page: params?.per_page ?? 20,
        };
      }

      console.error('❌ Error obteniendo contenido guardado:', error);
      throw new Error(`Error al obtener contenido guardado: ${error.message}`);
    }
  }

  /**
   * 🔍 Obtener contenido guardado por ID
   */
  async getSavedContentById(id: string): Promise<SavedContent> {
    logger.info('🔍 SavedContentService: Obteniendo contenido por ID:', id);

    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'GET',
        headers: authManager.getAuthHeaders(),
      });

      if (response.status === 404) {
        console.warn(`ℹ️ SavedContentService: Contenido ${id} no encontrado (404).`);
        return {
          id,
          user_id: '',
          title: '',
          description: undefined,
          tags: [],
          studio_id: '',
          studio_name: '',
          studio_type: '',
          content: null,
          meta_data: {},
          document_ids: [],
          document_names: [],
          is_shared: false,
          created_at: '',
          updated_at: '',
        } as SavedContent;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.info('✅ Contenido obtenido:', data);
      return data as SavedContent;
    } catch (error: any) {
      console.error('❌ Error obteniendo contenido por ID:', error);
      throw new Error(`Error al obtener contenido: ${error.message}`);
    }
  }

  /**
   * 💾 Guardar nuevo contenido
   */
  async saveContent(data: SavedContentCreateRequest): Promise<SavedContent> {
    logger.info('💾 SavedContentService: Guardando contenido...');
    logger.info('📋 Datos:', data);

    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: authManager.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      logger.info('✅ Contenido guardado:', result);
      return result as SavedContent;
    } catch (error: any) {
      console.error('❌ Error guardando contenido:', error);
      throw new Error(`Error al guardar contenido: ${error.message}`);
    }
  }

  /**
   * ✏️ Actualizar contenido guardado
   */
  async updateSavedContent(id: string, data: SavedContentUpdateRequest): Promise<SavedContent> {
    logger.info('✏️ SavedContentService: Actualizando contenido:', id);
    logger.info('📋 Datos:', data);

    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'PUT',
        headers: authManager.getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      logger.info('✅ Contenido actualizado:', result);
      return result as SavedContent;
    } catch (error: any) {
      console.error('❌ Error actualizando contenido:', error);
      throw new Error(`Error al actualizar contenido: ${error.message}`);
    }
  }

  /**
   * 🗑️ Eliminar contenido guardado
   */
  async deleteSavedContent(id: string): Promise<{ success: boolean; message: string }> {
    logger.info('🗑️ SavedContentService: Eliminando contenido:', id);

    try {
      const response = await fetch(`${this.baseUrl}/${id}`, {
        method: 'DELETE',
        headers: authManager.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      logger.info('✅ Contenido eliminado:', result);
      return result as { success: boolean; message: string };
    } catch (error: any) {
      console.error('❌ Error eliminando contenido:', error);
      throw new Error(`Error al eliminar contenido: ${error.message}`);
    }
  }

  /**
   * 🔍 Buscar contenido guardado
   */
  async searchSavedContent(query: {
    search?: string;
    studio_ids?: string[];
    tags?: string[];
    date_from?: string;
    date_to?: string;
    page?: number;
    per_page?: number;
  }): Promise<SavedContentListResponse> {
    logger.info('🔍 SavedContentService: Buscando contenido...');
    logger.info('📋 Query:', query);

    try {
      const response = await fetch(`${this.baseUrl}/search`, {
        method: 'POST',
        headers: authManager.getAuthHeaders(),
        body: JSON.stringify(query),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      logger.info('✅ Resultados de búsqueda:', result);
      return result as SavedContentListResponse;
    } catch (error: any) {
      console.error('❌ Error buscando contenido:', error);
      throw new Error(`Error al buscar contenido: ${error.message}`);
    }
  }

  /**
   * 🏷️ Obtener tags únicos
   */
  async getUniqueTags(): Promise<string[]> {
    logger.info('🏷️ SavedContentService: Obteniendo tags únicos...');

    try {
      const response = await this.getSavedContent();
      const allTags = response.content.flatMap((item) => item.tags || []);
      const uniqueTags = [...new Set(allTags)];

      logger.info('✅ Tags únicos:', uniqueTags);
      return uniqueTags;
    } catch (error: any) {
      console.error('❌ Error obteniendo tags:', error);
      return [];
    }
  }

  /**
   * 📊 Obtener estadísticas de contenido guardado
   */
  async getContentStats(): Promise<{
    total: number;
    by_studio: Record<string, number>;
    by_type: Record<string, number>;
    recent_count: number;
  }> {
    logger.info('📊 SavedContentService: Obteniendo estadísticas...');

    try {
      const response = await this.getSavedContent();
      const content = response.content;

      const stats = {
        total: content.length,
        by_studio: {} as Record<string, number>,
        by_type: {} as Record<string, number>,
        recent_count: 0,
      };

      // Contar por studio y tipo
      content.forEach((item) => {
        stats.by_studio[item.studio_name] = (stats.by_studio[item.studio_name] || 0) + 1;
        stats.by_type[item.studio_type] = (stats.by_type[item.studio_type] || 0) + 1;
      });

      // Contar recientes (últimos 7 días)
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      stats.recent_count = content.filter((item) => new Date(item.created_at) > weekAgo).length;

      logger.info('✅ Estadísticas:', stats);
      return stats;
    } catch (error: any) {
      console.error('❌ Error obteniendo estadísticas:', error);
      return {
        total: 0,
        by_studio: {},
        by_type: {},
        recent_count: 0,
      };
    }
  }
}

// Exportar instancia singleton
export const savedContentService = new SavedContentService();
export default savedContentService;
